import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { TARGETING_MODES, activeTargetingPlatforms, withCampaignWideTargetingFallback } from '../targeting'
import { actions as rangeLocationActions, shouldOverrideLocalState, splitLocationsByPlatform } from './rangeLocationsReducer'
import { useTargeting } from '../TargetingContext'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { derivePlatformSelectionFromSetup } from 'components/Recommendations/Campaign/platform'

import { SecondaryButton } from 'components/Shared/Button'
import RangeLocations from './RangeLocations'
import RangeLocationsContextProvider, {
  useRangeLocations,
  useRangeLocationsDispatch,
} from './RangeLocationsContextProvider'

const propTypes = {
  tc: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  hasTargetingLimitations: PropTypes.bool,
  targetingMode: PropTypes.string,
  isFirstTc: PropTypes.bool,
}

function RangeLocationsListContent({
  tc,
  onChange,
  disabled = false,
  hasTargetingLimitations = false,
  targetingMode,
  isFirstTc = false,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const { value: targeting } = useTargeting()
  const tcTargeting = useMemo(() => targeting.find((x) => x.tc === tc), [targeting, tc])
  const { setup } = useCampaignSetup()
  const platforms = useMemo(() => activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)), [setup])
  const { locations } = useRangeLocations()
  const dispatch = useRangeLocationsDispatch()
  // See RangeLocationsContextProvider's identical fallback for why.
  const effectiveTcTargeting = useMemo(
    () => withCampaignWideTargetingFallback(tcTargeting, setup, isFirstTc),
    [tcTargeting, setup, isFirstTc],
  )

  const onRangeLocationsChange = useCallback(
    (locs) => {
      locs = locs || locations
      const payload = {}
      for (const platform of platforms) {
        payload[platform] = {
          ...tcTargeting?.[platform],
          targeting_mode: targetingMode,
          range_locations: splitLocationsByPlatform(locs, platform),
        }
      }
      onChange(payload)
    },
    [onChange, locations, platforms, targetingMode, tcTargeting],
  )

  useEffect(() => {
    // Listen to changes to tcTargeting and overwrite the local context but only on closed
    // This allows us to propagate valid range locations targeting settings to other tcs
    // that don't have a valid targting setting yet.
    if (!isOpen && effectiveTcTargeting && shouldOverrideLocalState(effectiveTcTargeting, locations)) {
      dispatch({ type: rangeLocationActions.reset, payload: { tcTargeting: effectiveTcTargeting } })
    }
  }, [effectiveTcTargeting, isOpen, locations, dispatch])

  if (targetingMode !== TARGETING_MODES.rangeLocations) {
    return null
  }

  return (
    <div className="range-locations">
      {isOpen && (
        <RangeLocations
          tc={tc}
          isOpen={isOpen}
          onSave={onRangeLocationsChange}
          setOpen={(open) => setIsOpen(open)}
          hasTargetingLimitations={hasTargetingLimitations}
          isFirstTc={isFirstTc}
        />
      )}
      <SecondaryButton
        text={t('common.edit')}
        color="navy"
        size="small"
        fullWidth={false}
        type="button"
        classNames="geo-targeting__button"
        onClick={(e) => {
          e.preventDefault()
          setIsOpen(!isOpen)
        }}
        disabled={disabled}
      />
    </div>
  )
}

RangeLocationsListContent.propTypes = propTypes

export default function RangeLocationsList(props) {
  return (
    <RangeLocationsContextProvider tc={props.tc} isFirstTc={props.isFirstTc}>
      <RangeLocationsListContent {...props} />
    </RangeLocationsContextProvider>
  )
}

RangeLocationsList.propTypes = propTypes
